import { isAuthenticatedUser } from 'js/lib/user';

import {
  LEARNING_GOAL_OPTIONS,
  type LEARNING_GOAL_OPTIONS_TYPE,
  ONBOARDING_BASE_URL,
} from 'bundles/onboarding-common/constants';
import {
  QUESTION_IDS,
  type QUESTION_IDS_TYPE,
  getQuestionPaths,
} from 'bundles/onboarding-common/constants/questionScheme';
import useFetchData from 'bundles/onboarding-common/hooks/useFetchData';
import useLearningGoalData from 'bundles/onboarding-common/hooks/useLearningGoalData';
import { showPathwaysUserBased } from 'bundles/unified-common/utils/showPathways';

const useBuildOnboardingUrl = ({
  isFirstTimeSeeingOnboarding,
  questionId,
  existingLearner,
  referrer,
  learningGoal: learningGoalFromProps,
}: {
  isFirstTimeSeeingOnboarding?: boolean;
  questionId?: QUESTION_IDS_TYPE;
  existingLearner?: boolean;
  referrer?: string;
  learningGoal?: LEARNING_GOAL_OPTIONS_TYPE | null;
}) => {
  const isNonSurveyPathwaysOnboarding = showPathwaysUserBased();
  const isUnauthenticatedUser = !isAuthenticatedUser();
  const shouldSkipQuery =
    !isNonSurveyPathwaysOnboarding || isFirstTimeSeeingOnboarding || isUnauthenticatedUser || !!learningGoalFromProps;
  const { value: learningGoalFromApi, loading } = useFetchData({
    useQueryData: useLearningGoalData,
    skip: shouldSkipQuery,
    questionIdForLocalStorage: QUESTION_IDS.learningGoal,
  });
  const QUESTION_PATHS = getQuestionPaths();
  const learningGoalData = learningGoalFromApi || learningGoalFromProps;

  if (!isNonSurveyPathwaysOnboarding && !isUnauthenticatedUser) {
    // If not in the rollout, and authenticated we will return the default onboarding URL
    return {
      loading: false,
      onboardingUrl: isFirstTimeSeeingOnboarding
        ? `${ONBOARDING_BASE_URL}?isFirstTimeSeeingOnboarding=true`
        : ONBOARDING_BASE_URL,
    };
  }

  const searchParams = new URLSearchParams();

  if (isFirstTimeSeeingOnboarding) {
    searchParams.append('isFirstTimeSeeingOnboarding', 'true');
  }

  if (existingLearner) {
    searchParams.append('existingLearner', 'true');
  }

  if (learningGoalData) {
    searchParams.append(QUESTION_IDS.learningGoal, learningGoalData);
  }

  if (referrer) {
    searchParams.append('referrer', referrer);
  }

  let questionIdParam: QUESTION_IDS_TYPE = QUESTION_PATHS.default[0];

  if (
    learningGoalData === LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS &&
    questionId === QUESTION_IDS.roleSelect
  ) {
    // If the user is an advancer and we are directing to the role select question,
    // we want to make sure that they see the advancer role select question
    questionIdParam = QUESTION_IDS.roleSelectAdvancer;
  } else if (
    questionId === QUESTION_IDS.roleSelectAdvancer &&
    learningGoalData !== LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS
  ) {
    // If the user is not an advancer and we are directing to the advancer role select question,
    // we want to make sure that they see the default role select question
    questionIdParam = QUESTION_IDS.roleSelect;
  } else if (questionId) {
    questionIdParam = questionId;
  }

  const searchParamsString = searchParams.toString() ? `?${searchParams.toString()}` : '';

  const onboardingUrl = `${ONBOARDING_BASE_URL}/${questionIdParam}${searchParamsString}`;

  return {
    loading,
    onboardingUrl,
  };
};

export default useBuildOnboardingUrl;
